// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { ChevronRight12 } from '@mysten/icons';
import {
    type ColumnDef,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    type SortingState,
    useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { useMemo, useState } from 'react';

export interface TableCardProps<DataType extends object> {
    refetching?: boolean;
    data: DataType[];
    columns: ColumnDef<DataType>[];
    sortTable?: boolean;
    defaultSorting?: SortingState;
}

type ColumnMeta = {
    headerClassName?: string;
    cellClassName?: string;
    align?: 'left' | 'right' | 'center';
};

function columnMeta(column: { columnDef: { meta?: unknown } }): ColumnMeta {
    return (column.columnDef.meta as ColumnMeta | undefined) ?? {};
}

function AscDescIcon({ sorting }: { sorting: 'asc' | 'desc' }) {
    return (
        <ArrowRight12
            fill="currentColor"
            className={clsx(sorting === 'asc' ? '-rotate-90' : 'rotate-90', ' text-steel-darker')}
        />
    );
}

// 📱 H5 专用：高颜值微光卡片组件
function MobileFoldableRow<DataType extends object>({ row }: { row: any }) {
    const [isOpen, setIsOpen] = useState(false);
    const cells = row.getVisibleCells();

    const renderCell = (cell: (typeof cells)[number]) =>
        flexRender(cell.column.columnDef.cell, cell.getContext());

    // 三列榜（# / 地址 / 数值）：单行左地址、右数值，无需右对齐地址
    if (cells.length === 3) {
        const [rankCell, primaryCell, valueCell] = cells;
        return (
            <div className="mb-3 flex items-center gap-2 rounded-xl border border-white/5 bg-[#1E222F]/40 px-3 py-3.5 shadow-md backdrop-blur-sm">
                <div className="w-8 shrink-0 text-center text-sm font-semibold tabular-nums text-[#2dd7a7]">
                    {renderCell(rankCell)}
                </div>
                <div className="min-w-0 flex-1 text-left text-xs font-medium text-[#38BDF8]">
                    {renderCell(primaryCell)}
                </div>
                <div className="shrink-0 max-w-[45%] text-right text-xs tabular-nums text-[#aeb9d0]">
                    {renderCell(valueCell)}
                </div>
            </div>
        );
    }

    const firstCell = cells[0];
    const secondCell = cells[1];
    const remainingCells = cells.slice(2);
    const secondAlign = secondCell ? columnMeta(secondCell.column).align : 'left';

    return (
        <div className={clsx(
            "border rounded-xl bg-[#1E222F]/40 mb-3 overflow-hidden transition-all duration-300 backdrop-blur-sm",
            isOpen ? "border-[#38BDF8]/40 shadow-[0_4px_20px_rgba(56,189,248,0.08)] bg-[#1E222F]/70" : "border-white/5 shadow-md"
        )}>
            <div 
                className="p-4 flex items-center justify-between gap-3 cursor-pointer select-none active:bg-white/5"
                onClick={() => setIsOpen(!isOpen)}
            >
                <div className="flex flex-1 items-center gap-2 min-w-0">
                    <div className="shrink-0 text-sm font-semibold text-[#2dd7a7] truncate font-mono">
                        {firstCell ? renderCell(firstCell) : null}
                    </div>
                    {secondCell && (
                        <div
                            className={clsx(
                                'min-w-0 flex-1 truncate text-xs font-medium',
                                secondAlign === 'right' ? 'text-right text-[#aeb9d0]' : 'text-left text-[#38BDF8]',
                            )}
                        >
                            {renderCell(secondCell)}
                        </div>
                    )}
                </div>
                
                <div className={clsx(
                    "w-6 h-6 rounded-full bg-white/5 flex items-center justify-center text-[#aeb9d0] transition-transform duration-300 shrink-0", 
                    isOpen ? "rotate-90 bg-[#38BDF8]/20 text-[#38BDF8]" : ""
                )}>
                    <ChevronRight12 className="w-3 h-3" />
                </div>
            </div>

            {remainingCells.length > 0 ? (
            <div 
                className={clsx(
                    "transition-all duration-300 ease-in-out",
                    isOpen ? "max-h-[800px] opacity-100 border-t border-white/5" : "max-h-0 opacity-0 pointer-events-none overflow-hidden"
                )}
            >
                <div className="p-4 bg-black/10 grid grid-cols-2 gap-3">
                    {remainingCells.map(({ column, id, getContext }) => (
                        <div 
                            key={id} 
                            className="flex flex-col gap-1 bg-[#0F172A]/40 border border-white/5 p-3 rounded-lg min-w-0"
                        >
                            <span className="block text-[10px] text-[#aeb9d0] uppercase tracking-wider font-semibold truncate max-w-full">
                                {typeof column.columnDef.header === 'string' 
                                    ? column.columnDef.header 
                                    : column.id}
                            </span>
                            <span className="text-xs text-[#aeb9d0] font-mono break-all line-clamp-2">
                                {flexRender(column.columnDef.cell, getContext())}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
            ) : null}
        </div>
    );
}

export function TableCard<DataType extends object>({
    refetching,
    data,
    columns,
    sortTable,
    defaultSorting,
}: TableCardProps<DataType>) {
    const [sorting, setSorting] = useState<SortingState>(defaultSorting || []);

    const processedcol = useMemo<ColumnDef<DataType>[]>(
        () =>
            columns.map((column) => ({
                ...column,
                ...(!sortTable && { cell: ({ getValue }) => getValue() }),
            })),
        [columns, sortTable],
    );

    const table = useReactTable({
        data,
        columns: processedcol,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: setSorting,
        enableSorting: !!sortTable,
        enableSortingRemoval: false,
        initialState: { sorting },
        state: { sorting },
    });

    return (
        <div className={clsx('w-full pb-8 px-0 md:px-1 py-1', refetching && 'opacity-50')}>
            
            {/* 💻 情况 A：PC 端大屏幕显示 - 保持标准的完整原生表格 */}
            <div className="hidden md:block w-full overflow-x-auto">
                <table className="w-full table-fixed border-collapse">
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(({ id, colSpan, column, isPlaceholder, getContext }) => {
                                    const { headerClassName, align = 'left' } = columnMeta(column);
                                    return (
                                    <th
                                        key={id}
                                        colSpan={colSpan}
                                        scope="col"
                                        className={clsx(
                                            'h-7.5 text-subtitle font-semibold uppercase text-[#aeb9d0]',
                                            align === 'right' && 'text-right',
                                            align === 'center' && 'text-center',
                                            align === 'left' && 'text-left',
                                            headerClassName,
                                        )}
                                        onClick={
                                            column.columnDef.enableSorting ? column.getToggleSortingHandler() : undefined
                                        }
                                    >
                                        <div
                                            className={clsx(
                                                'flex items-center gap-1',
                                                align === 'right' && 'justify-end',
                                                align === 'center' && 'justify-center',
                                                column.columnDef.enableSorting && 'cursor-pointer text-[#aeb9d0]',
                                            )}
                                        >
                                            {isPlaceholder ? null : flexRender(column.columnDef.header, getContext())}

                                            {column.getIsSorted() && (
                                                <AscDescIcon sorting={column.getIsSorted() as 'asc' | 'desc'} />
                                            )}
                                        </div>
                                    </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr key={row.id} className="group">
                                {row.getVisibleCells().map(({ column, id, getContext }, index) => {
                                    const { cellClassName, align = 'left' } = columnMeta(column);
                                    return (
                                    <td
                                        key={id}
                                        className={clsx(
                                            'h-7.5 text-body text-[#aeb9d0] group-hover:bg-white/10 align-middle',
                                            align === 'right' && 'text-right',
                                            align === 'center' && 'text-center',
                                            align === 'left' && 'text-left',
                                            'first:text-[#2dd7a7]',
                                            index === 1 && align === 'left' && 'text-[#8ed5ff]',
                                            cellClassName,
                                        )}
                                    >
                                        {flexRender(column.columnDef.cell, getContext())}
                                    </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* 📱 情况 B：H5 移动端显示 - 升级版高颜值数据 Bento 卡片组 */}
            <div className="block md:hidden w-full">
                {table.getRowModel().rows.length > 0 ? (
                    table.getRowModel().rows.map((row) => (
                        <MobileFoldableRow key={row.id} row={row} />
                    ))
                ) : (
                    <div className="text-center py-12 text-sm text-[#aeb9d0]/40 font-medium">
                        暂无匹配数据
                    </div>
                )}
            </div>

        </div>
    );
}